<!-- eslint-disable vue/no-v-html -->
<template>
  <TheNotification :is-open="isShowPopUp">
    <div class="p-4">
      <div class="relative mb-4">
        <strong class="block pr-6 text-xl">
          {{ contextTitle }}
        </strong>
        <button class="absolute right-0 top-1 h-6 w-6" @click="handleClose">
          <IconClose class="h-full w-full text-black" />
        </button>
      </div>
      <p
        class="mb-8 text-sm text-gray-600"
        v-html="decode(contextDescription)"
      />
      <ul>
        <li v-if="errorType === EnumErrorType.A">
          <a
            :href="switchButtonPath"
            :title="switchButtonText"
            class="text-blue-400 hover:underline"
          >
            {{ switchButtonText }}
          </a>
        </li>
        <li v-if="errorType === EnumErrorType.A">
          <button
            class="hover:text-blue-400 hover:underline"
            :title="stayButtonText"
            @click="handleClose"
          >
            {{ stayButtonText }}
          </button>
        </li>
        <li
          v-if="errorType === EnumErrorType.B || errorType === EnumErrorType.D"
        >
          <TheNuxtLink
            :to="`/${locale}/marketplace/help/technical-support`"
            :title="contactUsText"
            class="hover:text-blue-400 hover:underline"
            @click="handleClose"
          >
            {{ contactUsText }}
          </TheNuxtLink>
        </li>
      </ul>
    </div>
  </TheNotification>
</template>

<script setup lang="ts">
  import { decode } from 'entities';

  import { EnumRegionErrorType as EnumErrorType } from '@/stores/mainStore';

  const runtimeConfig = useRuntimeConfig();
  const { ENVIRONMENT } = runtimeConfig.public;

  const route = useRoute();
  const { t, locale } = useI18n({ useScope: 'local' });
  const mainStore = useMainStore();
  const { regionCode, regionData, matchedLocale } = storeToRefs(mainStore);
  const { isCN, currentRegion } = useLanguage();
  const { setClosedPopUpRegions } = useClosedPopUpRegions();

  /**
   * @name 錯誤類型
   * @see https://advantech-curation.visualstudio.com/Marketplace/_wiki/wikis/Marketplace.wiki/1273/1-1.%E7%94%A8%E6%88%B6%E6%9C%AA%E7%99%BB%E5%85%A5%E4%B9%8B%E8%AA%9E%E7%B3%BB%E8%88%87%E5%8D%80%E5%9F%9F%E5%88%A4%E6%96%B7%E8%A6%8F%E6%A0%BC?anchor=4.-%E7%95%B0%E5%B8%B8%E8%99%95%E7%90%86
   */

  const errorType = computed(
    (): EnumErrorType | null => regionData.value.errorType
  );

  const isShowPopUp = computed((): boolean => errorType.value !== null);

  const contextTitle = computed((): string => {
    switch (errorType.value) {
      case EnumErrorType.A:
        return t('type-A_title');
      case EnumErrorType.B:
        return t('type-B_title');
      case EnumErrorType.D:
        return t('type-D_title');
      default:
        return '';
    }
  });

  const contextDescription = computed((): string => {
    const region = provideRegionValueName(regionCode.value);
    return t(`type-${errorType.value}_description`, { region });
  });

  const contactUsText = computed((): string => {
    switch (errorType.value) {
      case EnumErrorType.B:
        return t('type-B_contact-us');
      case EnumErrorType.D:
        return t('type-D_contact-us');
      default:
        return '';
    }
  });

  const stayButtonText = computed((): string => {
    switch (errorType.value) {
      case EnumErrorType.A:
        return t('type-A_stay');
      default:
        return '';
    }
  });

  const switchButtonText = computed((): string => {
    switch (errorType.value) {
      case EnumErrorType.A:
        return t('type-A_switch');
      default:
        return '';
    }
  });

  const switchButtonPath = computed((): string => {
    const locale =
      !regionCode.value && isCN.value ? EnumLocale.EN_INT : matchedLocale.value;
    const domain = provideDomain(locale, ENVIRONMENT);
    const path = replaceLocaleInPath(route.path, locale);
    return `${domain}${path}`;
  });

  const provideRegionValueName = (code: EnumRegion | null): string => {
    if (code === null && isCN.value) {
      return t('region-site_int');
    }

    if (code === null) {
      return '';
    }

    switch (code) {
      case EnumRegion.AMERICAS:
        return t('region-site_americas');
      case EnumRegion.EMEA:
        return t('region-site_emea');
      case EnumRegion.CN:
        return t('region-site_cn');
      case EnumRegion.JP:
        return t('region-site_jp');
      case EnumRegion.KR:
        return t('region-site_kr');
      case EnumRegion.TW:
        return isCN.value ? '所在地' : t('region-site_tw');
      case EnumRegion.ASIA_PACIFIC:
        return t('region-site_others');
      case EnumRegion.INT:
        return t('region-site_int');
      default:
        return code;
    }
  };

  const handleClose = (): void => {
    if (regionData.value.errorType === EnumRegionErrorType.A) {
      setClosedPopUpRegions(currentRegion.value);
    }
    regionData.value.errorType = null;
  };
</script>

<i18n lang="json">
{
  "en-int": {
    "type-A_title": "Looking for a local experience?",
    "type-A_description": "Would you like to visit the {region} site for a more relevant experience?",
    "type-A_switch": "Visit now",
    "type-A_stay": "Stay here",
    "type-B_title": "Experience not supported",
    "type-B_description": "We're expanding globally. You've been redirected to the {region} site for the best experience.<br/><br/>Need help? Contact us anytime.",
    "type-B_contact-us": "Contact us",
    "type-D_title": "We've redirected you based on your account",
    "type-D_description": "Your account is currently linked to {region}, so we've taken you to the best site for your region.<br/><br/>Need help? Contact us — we're here for you.",
    "type-D_contact-us": "Contact us",
    "region-site_americas": "Americas",
    "region-site_emea": "Europe, Middle East & Africa",
    "region-site_cn": "China",
    "region-site_jp": "Japan",
    "region-site_kr": "Korea",
    "region-site_tw": "Taiwan",
    "region-site_others": "Others",
    "region-site_int": "Global"
  },
  "en-jp": {
    "type-A_title": "Looking for a local experience?",
    "type-A_description": "Would you like to visit the {region} site for a more relevant experience?",
    "type-A_switch": "Visit now",
    "type-A_stay": "Stay here",
    "type-B_title": "Experience not supported",
    "type-B_description": "We're expanding globally. You've been redirected to the {region} site for the best experience.<br/><br/>Need help? Contact us anytime.",
    "type-B_contact-us": "Contact us",
    "type-D_title": "We've redirected you based on your account",
    "type-D_description": "Your account is currently linked to {region}, so we've taken you to the best site for your region.<br/><br/>Need help? Contact us — we're here for you.",
    "type-D_contact-us": "Contact us",
    "region-site_americas": "Americas",
    "region-site_emea": "Europe, Middle East & Africa",
    "region-site_cn": "China",
    "region-site_jp": "Japan",
    "region-site_kr": "Korea",
    "region-site_tw": "Taiwan",
    "region-site_others": "Others",
    "region-site_int": "Global"
  },
  "en-tw": {
    "type-A_title": "Looking for a local experience?",
    "type-A_description": "Would you like to visit the {region} site for a more relevant experience?",
    "type-A_switch": "Visit now",
    "type-A_stay": "Stay here",
    "type-B_title": "Experience not supported",
    "type-B_description": "We're expanding globally. You've been redirected to the {region} site for the best experience.<br/><br/>Need help? Contact us anytime.",
    "type-B_contact-us": "Contact us",
    "type-D_title": "We've redirected you based on your account",
    "type-D_description": "Your account is currently linked to {region}, so we've taken you to the best site for your region.<br/><br/>Need help? Contact us — we're here for you.",
    "type-D_contact-us": "Contact us",
    "region-site_americas": "Americas",
    "region-site_emea": "Europe, Middle East & Africa",
    "region-site_cn": "China",
    "region-site_jp": "Japan",
    "region-site_kr": "Korea",
    "region-site_tw": "Taiwan",
    "region-site_others": "Others",
    "region-site_int": "Global"
  },
  "en-kr": {
    "type-A_title": "Looking for a local experience?",
    "type-A_description": "Would you like to visit the {region} site for a more relevant experience?",
    "type-A_switch": "Visit now",
    "type-A_stay": "Stay here",
    "type-B_title": "Experience not supported",
    "type-B_description": "We're expanding globally. You've been redirected to the {region} site for the best experience.<br/><br/>Need help? Contact us anytime.",
    "type-B_contact-us": "Contact us",
    "type-D_title": "We've redirected you based on your account",
    "type-D_description": "Your account is currently linked to {region}, so we've taken you to the best site for your region.<br/><br/>Need help? Contact us — we're here for you.",
    "type-D_contact-us": "Contact us",
    "region-site_americas": "Americas",
    "region-site_emea": "Europe, Middle East & Africa",
    "region-site_cn": "China",
    "region-site_jp": "Japan",
    "region-site_kr": "Korea",
    "region-site_tw": "Taiwan",
    "region-site_others": "Others",
    "region-site_int": "Global"
  },
  "en-americas": {
    "type-A_title": "Looking for a local experience?",
    "type-A_description": "Would you like to visit the {region} site for a more relevant experience?",
    "type-A_switch": "Visit now",
    "type-A_stay": "Stay here",
    "type-B_title": "Experience not supported",
    "type-B_description": "We're expanding globally. You've been redirected to the {region} site for the best experience.<br/><br/>Need help? Contact us anytime.",
    "type-B_contact-us": "Contact us",
    "type-D_title": "We've redirected you based on your account",
    "type-D_description": "Your account is currently linked to {region}, so we've taken you to the best site for your region.<br/><br/>Need help? Contact us — we're here for you.",
    "type-D_contact-us": "Contact us",
    "region-site_americas": "Americas",
    "region-site_emea": "Europe, Middle East & Africa",
    "region-site_cn": "China",
    "region-site_jp": "Japan",
    "region-site_kr": "Korea",
    "region-site_tw": "Taiwan",
    "region-site_others": "Others",
    "region-site_int": "Global"
  },
  "en-asia-pacific": {
    "type-A_title": "Looking for a local experience?",
    "type-A_description": "Would you like to visit the {region} site for a more relevant experience?",
    "type-A_switch": "Visit now",
    "type-A_stay": "Stay here",
    "type-B_title": "Experience not supported",
    "type-B_description": "We're expanding globally. You've been redirected to the {region} site for the best experience.<br/><br/>Need help? Contact us anytime.",
    "type-B_contact-us": "Contact us",
    "type-D_title": "We've redirected you based on your account",
    "type-D_description": "Your account is currently linked to {region}, so we've taken you to the best site for your region.<br/><br/>Need help? Contact us — we're here for you.",
    "type-D_contact-us": "Contact us",
    "region-site_americas": "Americas",
    "region-site_emea": "Europe, Middle East & Africa",
    "region-site_cn": "China",
    "region-site_jp": "Japan",
    "region-site_kr": "Korea",
    "region-site_tw": "Taiwan",
    "region-site_others": "Others",
    "region-site_int": "Global"
  },
  "en-emea": {
    "type-A_title": "Looking for a local experience?",
    "type-A_description": "Would you like to visit the {region} site for a more relevant experience?",
    "type-A_switch": "Visit now",
    "type-A_stay": "Stay here",
    "type-B_title": "Experience not supported",
    "type-B_description": "We're expanding globally. You've been redirected to the {region} site for the best experience.<br/><br/>Need help? Contact us anytime.",
    "type-B_contact-us": "Contact us",
    "type-D_title": "We've redirected you based on your account",
    "type-D_description": "Your account is currently linked to {region}, so we've taken you to the best site for your region.<br/><br/>Need help? Contact us — we're here for you.",
    "type-D_contact-us": "Contact us",
    "region-site_americas": "Americas",
    "region-site_emea": "Europe, Middle East & Africa",
    "region-site_cn": "China",
    "region-site_jp": "Japan",
    "region-site_kr": "Korea",
    "region-site_tw": "Taiwan",
    "region-site_others": "Others",
    "region-site_int": "Global"
  },
  "ja-jp": {
    "type-A_title": "地域に合ったエクスペリエンスをお探しですか？",
    "type-A_description": "より適した情報をご覧になるため、{region}のサイトに移動しますか？",
    "type-A_switch": "今すぐサイトへ",
    "type-A_stay": "このまま続ける",
    "type-B_title": "このエクスペリエンスは現在ご利用の地域ではサポートされていません",
    "type-B_description": "当社はグローバル展開を進めています。最適なエクスペリエンスを提供するため、{region}のサイトにリダイレクトしました。",
    "type-B_contact-us": "お問い合わせ",
    "type-D_title": "アカウント情報に基づいてリダイレクトしました",
    "type-D_description": "お客様のアカウントは現在{region}に紐づけられているため、該当するサイトにご案内しています。",
    "type-D_contact-us": "お問い合わせ",
    "region-site_americas": "アメリカ大陸",
    "region-site_emea": "欧州・中東・アフリカ",
    "region-site_cn": "中国",
    "region-site_jp": "日本",
    "region-site_kr": "韓国",
    "region-site_tw": "台湾",
    "region-site_others": "その他の地域",
    "region-site_int": "グローバル"
  },
  "zh-cn": {
    "type-A_title": "想体验本地化内容吗？",
    "type-A_description": "是否前往 {region} 网站，获得更符合需求的体验？",
    "type-A_switch": "立即前往",
    "type-A_stay": "留在此网站",
    "type-B_title": "当前您所在的地区暂不支持此功能",
    "type-B_description": "我们正在拓展全球服务，已自动为您跳转至 {region} 网站，以提供最佳体验。<br/>需要帮助吗？欢迎随时联系我们。",
    "type-B_contact-us": "联系我们",
    "type-D_title": "我们已根据您的账户信息跳转至相应网站",
    "type-D_description": "您的账户当前设置为 {region}，因此已跳转至对应地区的网站。",
    "type-D_contact-us": "联系我们",
    "region-site_americas": "美洲地区",
    "region-site_emea": "欧洲、中东和非洲地区",
    "region-site_cn": "中国",
    "region-site_jp": "日本",
    "region-site_kr": "韩国",
    "region-site_tw": "台湾",
    "region-site_others": "其他地区",
    "region-site_int": "国际站"
  },
  "zh-tw": {
    "type-A_title": "想體驗在地化內容嗎？",
    "type-A_description": "是否前往 {region} 網站，獲得更符合需求的體驗？",
    "type-A_switch": "立即前往",
    "type-A_stay": "留在此網站",
    "type-B_title": "目前無法在您所在的地區使用此功能",
    "type-B_description": "我們正在拓展全球服務，已自動為您導向至 {region} 網站，以提供最佳體驗。<br/>需要協助嗎？歡迎隨時與我們聯繫。",
    "type-B_contact-us": "聯繫我們",
    "type-D_title": "我們已根據您的帳戶資訊導向適當網站",
    "type-D_description": "您的帳戶目前設定為 {region}，因此已導向至對應地區的網站。",
    "type-D_contact-us": "聯繫我們",
    "region-site_americas": "美洲地區",
    "region-site_emea": "歐洲、中東與非洲地區",
    "region-site_cn": "中國",
    "region-site_jp": "日本",
    "region-site_kr": "韓國",
    "region-site_tw": "台灣",
    "region-site_others": "其他地區",
    "region-site_int": "國際"
  }
}
</i18n>
